import type { QueryClient } from '@tanstack/react-query';

import { meAccessQueryOptions, sidebarQueryOptions, storeSidebar } from '../../../access';
import { clearStoredSidebar } from '../../../access/sidebar/sidebarItems';
import { resetDashboardSession } from '../../../store/dashboardSession';
import { notifyLocalSessionChanged } from '../../../session/localSession';
import localStorageStore from '../../../store/localStorageStore';
import { analytics } from '../../analytics';
import { LOGIN_SESSION_START } from '../../analytics/events';
import { trackEvent } from '../../analytics/trackingMiddleware';
import type { OpenedSession, SessionLayout } from '../types/domain.types';

export type LoginMethod = 'passcode' | 'password';

const sidebarOf = (layout: SessionLayout) => layout.menu ?? layout.sideBar;

const applyLayout = (layout: SessionLayout): void => {
  storeSidebar(sidebarOf(layout), layout.menu ? Date.now() : 0);
  localStorageStore.setItem('landingPage', layout.landingPage);
};

const forgetSomeoneElsesLayout = (session: OpenedSession): void => {
  if (session.layout !== null || localStorageStore.getItem('userId') === session.userId) return;
  clearStoredSidebar();
  localStorageStore.removeItem('landingPage');
};

export const openLocalSession = (session: OpenedSession, method: LoginMethod = 'passcode'): void => {
  localStorageStore.setItem('userId', session.userId);
  localStorageStore.setItem('userid', session.userId);
  localStorageStore.setItem('userName', session.userName);
  localStorageStore.setItem('email', session.email);
  localStorageStore.setItem('isStaff', JSON.stringify(session.isStaff));
  localStorageStore.setItem('role', '');
  if (session.layout !== null) applyLayout(session.layout);
  void analytics.identifyUser({ distinctId: session.userId, userProperties: { name: session.userName } });
  trackEvent(LOGIN_SESSION_START, { method });
};

export const startSession = (queryClient: QueryClient, session: OpenedSession, method: LoginMethod = 'passcode'): void => {
  queryClient.clear();
  resetDashboardSession();
  forgetSomeoneElsesLayout(session);
  if (session.access !== null) queryClient.setQueryData(meAccessQueryOptions().queryKey, session.access);
  if (session.layout?.menu) queryClient.setQueryData(sidebarQueryOptions().queryKey, session.layout.menu);
  openLocalSession(session, method);
  notifyLocalSessionChanged();
};

export const hasLocalSession = (): boolean => Boolean(localStorageStore.getItem('userId'));

export interface LocalPerson {
  readonly userId: string;
  readonly name: string;
}

export const localSessionPerson = (): LocalPerson | null => {
  const userId = localStorageStore.getItem('userId');
  return userId ? { userId, name: localStorageStore.getItem('userName') ?? '' } : null;
};
